import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor
api.interceptors.request.use(
  (config) => {
    // Try to get token from localStorage first
    let token = localStorage.getItem('accessToken');

    // If not found, try to get from auth-store (Zustand persist)
    if (!token) {
      try {
        const authStore = localStorage.getItem('auth-store');
        if (authStore) {
          const parsed = JSON.parse(authStore);
          token = parsed.state?.accessToken || parsed.accessToken;
        }
      } catch (e) {
        // Ignore parsing errors
      }
    }

    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor
let refreshingTokenPromise: Promise<string | null> | null = null;

const syncTokensToAuthStore = (accessToken: string, refreshToken: string) => {
  try {
    const authStoreRaw = localStorage.getItem('auth-store');
    if (authStoreRaw) {
      const parsed = JSON.parse(authStoreRaw);
      parsed.state = { ...parsed.state, accessToken, refreshToken };
      localStorage.setItem('auth-store', JSON.stringify(parsed));
    }
  } catch (e) {
    // Ignore parsing errors
  }
};

const refreshAccessToken = async (): Promise<string | null> => {
  const refreshToken = localStorage.getItem('refreshToken');
  if (!refreshToken) return null;
  try {
    const response = await axios.post(`${API_BASE_URL}/auth/refresh`, { refresh_token: refreshToken });
    const { access_token: newAccessToken, refresh_token: newRefreshToken } = response.data;
    localStorage.setItem('accessToken', newAccessToken);
    localStorage.setItem('refreshToken', newRefreshToken || refreshToken);
    localStorage.setItem('sessionLastActivity', String(Date.now()));
    syncTokensToAuthStore(newAccessToken, newRefreshToken || refreshToken);
    api.defaults.headers.common['Authorization'] = `Bearer ${newAccessToken}`;
    return newAccessToken;
  } catch (e) {
    return null;
  }
};

const forceLogoutToLogin = () => {
  localStorage.removeItem('accessToken');
  localStorage.removeItem('refreshToken');
  localStorage.removeItem('sessionLastActivity');
  const requestedPath = `${window.location.pathname}${window.location.search}${window.location.hash}`;
  const safeRequestedPath = requestedPath.startsWith('/') && !requestedPath.startsWith('//') ? requestedPath : '/my-profile';
  window.location.href = `/login?returnTo=${encodeURIComponent(safeRequestedPath)}`;
};

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config || {};
    const requestPath = originalRequest.url || '';
    const isAuthenticationAttempt = requestPath.endsWith('/auth/login') || requestPath.endsWith('/auth/register') || requestPath.endsWith('/auth/refresh');

    if (error.response?.status === 401 && !isAuthenticationAttempt && !originalRequest._retry) {
      originalRequest._retry = true;

      if (!refreshingTokenPromise) {
        refreshingTokenPromise = refreshAccessToken().finally(() => {
          refreshingTokenPromise = null;
        });
      }
      const newAccessToken = await refreshingTokenPromise;

      if (newAccessToken) {
        originalRequest.headers = { ...(originalRequest.headers || {}), Authorization: `Bearer ${newAccessToken}` };
        return api(originalRequest);
      }

      forceLogoutToLogin();
    }
    return Promise.reject(error);
  }
);

export default api;
